import { useState, useEffect, useRef } from "react";
import LazyVideo from "@/components/LazyVideo";
import { ArrowUpRight, Calendar, Trophy, ChevronLeft, ChevronRight, Globe, Instagram } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {if (e.isIntersecting) setInView(true);}, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

// Vídeo fixo da agenda de torneios
const TOURNAMENT_VIDEO = "/assets/video/torneio.mp4";

// Fallback usado apenas se a API não responder
const fallbackItems = [
{
  id: 1,
  posterVertical: "/assets/img/torneio-poster-desktop.webp",
  posterMobile: "/assets/img/torneio-poster-mobile.webp",
  title: "Open Real Taberna",
  subtitle: "Inscrições Abertas",
  date: "07 a 12 de Julho 2025",
  categories: "Categorias: M1 a M6, F3 a F6, MX3 a MX5",
  deadline: "Inscrições até 05 de Julho em padelteams.pt",
  ctaText: "Saber mais e inscrever",
  ctaLink: "https://padelteams.pt/info/competition?k=Y2lkPTcxOTI=",
  video: TOURNAMENT_VIDEO
}];

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function TournamentSponsors({ sponsors, compact }) {
  const list = (sponsors || []).filter((s) => s.name);
  if (list.length === 0) return null;
  return (
    <div className={`border-t border-white/10 ${compact ? "mt-3 pt-3" : "mt-5 pt-4"}`} data-testid="tournament-sponsors">
      <span className={`font-body font-semibold uppercase tracking-widest text-white/40 block ${compact ? "text-[9px] mb-2" : "text-[10px] mb-3"}`}>Patrocinadores do Torneio</span>
      <div className="flex flex-wrap gap-2">
        {list.map((s, i) => (
          <div key={i} className={`inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full ${compact ? "px-3 py-1.5" : "px-4 py-2"}`} data-testid={`tournament-sponsor-${i}`}>
            <span className={`font-body font-medium text-white/70 ${compact ? "text-[11px]" : "text-xs"}`}>{s.name}</span>
            {s.website_url && (
              <a href={s.website_url} target="_blank" rel="noopener noreferrer" aria-label={`Site de ${s.name}`} className="text-white/40 hover:text-jp-lime transition-colors" data-testid={`sponsor-site-link-${i}`}>
                <Globe size={compact ? 12 : 14} />
              </a>
            )}
            {s.social_url && (
              <a href={s.social_url} target="_blank" rel="noopener noreferrer" aria-label={`Rede social de ${s.name}`} className="text-white/40 hover:text-jp-lime transition-colors" data-testid={`sponsor-social-link-${i}`}>
                <Instagram size={compact ? 12 : 14} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Injeta schema.org SportsEvent para os torneios com data de início (Rich Results do Google)
function injectEventSchema(items) {
  const events = items
    .filter((i) => i.startDate)
    .map((i) => ({
      "@context": "https://schema.org",
      "@type": "SportsEvent",
      name: i.title,
      sport: "Padel",
      startDate: i.startDate,
      ...(i.endDate ? { endDate: i.endDate } : {}),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      description: [i.subtitle, i.categories, i.deadline].filter(Boolean).join(". "),
      ...(i.posterVertical
        ? { image: [i.posterVertical.startsWith("http") ? i.posterVertical : `https://justpadelcenter.pt${i.posterVertical}`] }
        : {}),
      location: {
        "@type": "Place",
        name: "Just Padel Center",
        address: {
          "@type": "PostalAddress",
          streetAddress: "R. do Seixo 735",
          addressLocality: "Arcozelo, Barcelos",
          postalCode: "4750-778",
          addressCountry: "PT",
        },
      },
      organizer: { "@type": "Organization", name: "Just Padel Center", url: "https://justpadelcenter.pt/" },
      ...(i.ctaLink ? { offers: { "@type": "Offer", url: i.ctaLink, availability: "https://schema.org/InStock" } } : {}),
    }));
  document.getElementById("tournament-events-schema")?.remove();
  if (events.length === 0) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "tournament-events-schema";
  script.text = JSON.stringify(events);
  document.head.appendChild(script);
}

export default function Highlights() {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [agendaItems, setAgendaItems] = useState(fallbackItems);

  // Carregar torneios da API (geridos no backoffice)
  useEffect(() => {
    fetch(`${API_URL}/api/tournaments.php`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const items = data.map((t) => ({
            id: t.id,
            posterVertical: t.poster_desktop,
            posterMobile: t.poster_mobile,
            title: t.title,
            subtitle: t.subtitle,
            date: t.date_range,
            startDate: (t.start_date || "").slice(0, 10),
            endDate: (t.end_date || "").slice(0, 10),
            categories: t.categories,
            deadline: t.deadline,
            ctaText: t.cta_text,
            ctaLink: t.cta_link,
            video: TOURNAMENT_VIDEO,
            sponsors: Array.isArray(t.sponsors) ? t.sponsors : [],
          }));
          setAgendaItems(items);
          injectEventSchema(items);
        }
      })
      .catch(() => {});
  }, []);

  // Auto-rotate carousel every 5 seconds (only if more than one item)
  useEffect(() => {
    if (agendaItems.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % agendaItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [agendaItems.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % agendaItems.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + agendaItems.length) % agendaItems.length);

  const currentItem = agendaItems[currentSlide % agendaItems.length] || agendaItems[0];

  return (
    <section id="highlights" data-testid="highlights-section" ref={ref} className="py-20 bg-[#0a0f1a]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        
        {/* Section Header - Agenda */}
        <div className={`text-center mb-12 ${inView ? 'fade-in-up' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 bg-jp-lime/10 border border-jp-lime/20 rounded-full px-4 py-2 mb-6">
            <Trophy size={14} className="text-jp-lime" />
            <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime">AGENDA</span>
          </div>
        </div>

        {/* Mobile Layout - 3 blocks stacked vertically */}
        <div className={`lg:hidden ${inView ? 'fade-in-up' : 'opacity-0'}`}>
          {/* Block 1: Video */}
          <div className="rounded-2xl overflow-hidden border border-white/10 mb-4">
            <div className="aspect-[9/16]">
              <LazyVideo
                    src={currentItem.video}
                className="w-full h-full object-cover"
                    data-testid="agenda-video-mobile" />

            </div>
          </div>
          
          {/* Block 2: Poster Image (mobile version) */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0d1320] mb-4">
            <img
              src={currentItem.posterMobile || currentItem.posterVertical}
              alt={currentItem.title}
              width="640" height="799"
              className="w-full h-auto object-contain"
              loading="lazy" />

          </div>
          
          {/* Block 3: Info Card */}
          <div className="bg-gradient-to-br from-[#0e1525] to-[#131c2e] rounded-2xl p-4 border border-jp-lime/10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-jp-lime/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-jp-lime animate-pulse" />
                <span className="font-body text-[10px] font-semibold uppercase tracking-widest text-jp-lime">{currentItem.subtitle}</span>
              </div>
              
              <h2 className="font-display text-base text-white font-medium mb-1 leading-tight">
                {currentItem.title}
              </h2>
              
              <div className="flex items-center gap-1 mb-2 text-white/60">
                <Calendar size={12} className="text-jp-lime" />
                <span className="font-body text-xs font-medium">{currentItem.date}</span>
              </div>
              
              <p className="font-body text-white/40 text-[10px] leading-relaxed mb-2">
                {currentItem.categories}
              </p>
              {currentItem.deadline && (
                <p className="font-body text-jp-lime text-[10px] font-semibold mb-3">{currentItem.deadline}</p>
              )}
              {agendaItems.length > 1 && (
                <div className="flex items-center gap-2 mb-3">
                  <button onClick={prevSlide} aria-label="Torneio anterior" data-testid="agenda-prev-mobile" className="w-7 h-7 rounded-full bg-white/5 hover:bg-jp-lime/20 flex items-center justify-center text-white/60 transition-colors"><ChevronLeft size={14} /></button>
                  <span className="font-body text-[10px] text-white/40">{(currentSlide % agendaItems.length) + 1} / {agendaItems.length}</span>
                  <button onClick={nextSlide} aria-label="Próximo torneio" data-testid="agenda-next-mobile" className="w-7 h-7 rounded-full bg-white/5 hover:bg-jp-lime/20 flex items-center justify-center text-white/60 transition-colors"><ChevronRight size={14} /></button>
                </div>
              )}
            </div>
            
            {/* CTA Button */}
            <a
              href={currentItem.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-jp-lime text-black font-body font-semibold text-xs px-4 py-3 rounded-full hover:bg-jp-lime-dark transition-all flex items-center justify-center gap-2 group"
              data-testid="highlight-cta-mobile">

              {currentItem.ctaText || "Inscrever"}
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <TournamentSponsors sponsors={currentItem.sponsors} compact />
          </div>
        </div>

        {/* Desktop Layout */}
        <div className={`hidden lg:block ${inView ? 'fade-in-up' : 'opacity-0'}`}>
          {/* Top Row: Video + Poster side by side, same height */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            {/* Left: Video - same height as poster */}
            <div className="col-span-3">
              <div className="rounded-2xl overflow-hidden border border-white/10 h-full">
                <LazyVideo
                    src={currentItem.video}
                  className="w-full h-full object-cover"
                    data-testid="agenda-video" />

              </div>
            </div>
            
            {/* Right: Poster */}
            <div className="col-span-9">
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0d1320]">
                <img
                  src={currentItem.posterVertical}
                  alt={currentItem.title}
                  width="942" height="550"
                  className="w-full h-auto object-contain"
                  loading="lazy" />

              </div>
            </div>
          </div>
          
          {/* Bottom: Info Card - Full width */}
          <div className="bg-gradient-to-br from-[#0e1525] to-[#131c2e] rounded-2xl p-6 border border-jp-lime/10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-jp-lime/10 rounded-full blur-3xl" />
            
            <div className="flex items-center gap-8 relative">
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-jp-lime animate-pulse" />
                  <span className="font-body text-xs font-semibold uppercase tracking-widest text-jp-lime">{currentItem.subtitle}</span>
                </div>
                
                <h2 className="font-display text-2xl text-white font-medium mb-2 leading-tight">
                  {currentItem.title}
                </h2>
                
                <div className="flex items-center gap-2 text-white/60">
                  <Calendar size={16} className="text-jp-lime" />
                  <span className="font-body text-sm font-medium">{currentItem.date}</span>
                </div>
              </div>

              {/* Categories */}
              <div className="flex-1">
                <p className="font-body !text-base mb-1 text-white/40">
                  {currentItem.categories}
                </p>
                <p className="font-body !text-base text-jp-lime !font-semibold">
                  {currentItem.deadline}
                </p>
              </div>
              
              {/* CTA Button */}
              <a
                href={currentItem.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-jp-lime text-black font-body font-semibold text-sm px-6 py-3 rounded-full hover:bg-jp-lime-dark transition-all flex items-center gap-2 group flex-shrink-0"
                data-testid="highlight-cta">

                {currentItem.ctaText}
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              {agendaItems.length > 1 && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={prevSlide} aria-label="Torneio anterior" data-testid="agenda-prev" className="w-9 h-9 rounded-full bg-white/5 hover:bg-jp-lime/20 flex items-center justify-center text-white/60 transition-colors"><ChevronLeft size={16} /></button>
                  <span className="font-body text-xs text-white/40">{(currentSlide % agendaItems.length) + 1} / {agendaItems.length}</span>
                  <button onClick={nextSlide} aria-label="Próximo torneio" data-testid="agenda-next" className="w-9 h-9 rounded-full bg-white/5 hover:bg-jp-lime/20 flex items-center justify-center text-white/60 transition-colors"><ChevronRight size={16} /></button>
                </div>
              )}
            </div>
            <TournamentSponsors sponsors={currentItem.sponsors} />
          </div>
        </div>
      </div>
    </section>);

}